import { staleTimes, useMutation, useQuery } from '@platform/api-client';
import type { TypedClient } from '@platform/openapi';
import { queryKeys, usePageContext } from '../usePageContext.ts';
import { useKeysetPages } from '../useKeysetPages.ts';
import type { Notification } from './notifications.ts';

export const UNREAD_POLL_MS = 60_000;

const NOTIFICATIONS = 'notifications';

export type InboxCounts = { readonly unread: number; readonly unseen: number };

export const NO_COUNTS: InboxCounts = { unread: 0, unseen: 0 };

const countsKey = (tenantId: string) => queryKeys.notifications(tenantId, { counts: true });

export const useInboxCounts = (api: TypedClient): InboxCounts => {
  const { tenantId } = usePageContext();
  return (
    useQuery({
      queryKey: countsKey(tenantId),
      queryFn: () => api.get('/admin/notifications/unread-count', {}),
      refetchInterval: UNREAD_POLL_MS,
      staleTime: staleTimes.orders,
    }).data ?? NO_COUNTS
  );
};

export const useMarkSeen = (api: TypedClient) => {
  const { tenantId, queryClient, invalidate } = usePageContext();
  return useMutation({
    mutationFn: async (upTo: string): Promise<void> => {
      queryClient.setQueryData<InboxCounts>(countsKey(tenantId), (counts) =>
        counts === undefined ? counts : { ...counts, unseen: 0 },
      );
      await api.post('/admin/notifications/seen', { body: { upTo } });
    },
    onSettled: () => invalidate(NOTIFICATIONS),
  });
};

export const NOTIFICATION_FILTERS = ['all', 'unread'] as const;
export type NotificationFilter = (typeof NOTIFICATION_FILTERS)[number];

export const PANEL_PAGE_SIZE = 20;

export const useNotificationPages = (api: TypedClient, filter: NotificationFilter, pageSize: number) => {
  const { tenantId } = usePageContext();
  return useKeysetPages<Notification>({
    queryKey: queryKeys.notifications(tenantId, { filter, pageSize }),
    resource: 'orders',
    fetchPage: (pageToken) =>
      api.get('/admin/notifications', {
        query: {
          unread: filter === 'unread' ? 'true' : 'false',
          page_size: pageSize,
          ...(pageToken === null ? {} : { page_token: pageToken }),
        },
      }),
  });
};

export type ReadRequest =
  { readonly kind: 'one'; readonly notificationId: string } | { readonly kind: 'all'; readonly upTo: string };

export const useMarkRead = (api: TypedClient) => {
  const { invalidate } = usePageContext();
  return useMutation({
    mutationFn: async (request: ReadRequest): Promise<void> => {
      if (request.kind === 'one')
        await api.post('/admin/notifications/read', { body: { ids: [request.notificationId] } });
      else await api.post('/admin/notifications/read-all', { body: { upTo: request.upTo } });
    },
    onSettled: () => invalidate(NOTIFICATIONS),
  });
};
